import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  TrendingUp, TrendingDown, AlertTriangle, Lock, CheckCircle2, Clock,
  BarChart3, Activity, ShieldAlert, Eye, ArrowRight, RefreshCw, FileDown,
} from 'lucide-react';
import { useGerarRelatorioPdf } from '@/hooks/useGerarRelatorioPdf';
import MetricTooltip, { METRIC_EXPLANATIONS } from '@/components/system/MetricTooltip';
import { EmptyState } from '@/components/system/EmptyState';
import { EmpExportButton } from './EmpExportButton';
import { useEmpIndicadores, EmpIndicador, EmpDesvio, EmpAlerta } from '@/hooks/useEmpIndicadores';
import { useEmpExecucaoMutations } from '@/hooks/useEmpExecucao';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';

interface Props {
  contratoId: string;
  contratoNome: string;
  canEdit: boolean;
}

const fmt = (v: number | null, decimals = 2) => v !== null && v !== undefined ? v.toFixed(decimals) : '—';
const fmtDate = (d: string) => {
  try { return format(new Date(d), 'dd/MM/yy', { locale: ptBR }); } catch { return d; }
};
const fmtPeriodo = (d: string) => {
  try { return format(new Date(d + 'T00:00:00'), 'MMM/yy', { locale: ptBR }); } catch { return d; }
};

const spiColor = (v: number | null) => {
  if (v === null) return 'text-muted-foreground';
  if (v >= 1) return 'text-success';
  if (v >= 0.9) return 'text-warning';
  return 'text-destructive';
};

/** Semantic background for KPI cards based on EVM index value */
const evmCardBg = (v: number | null) => {
  if (v === null) return '';
  if (v >= 1) return 'bg-success/5 border-success/20';
  if (v >= 0.9) return 'bg-warning/5 border-warning/20';
  return 'bg-destructive/5 border-destructive/20';
};

const sevBadge = (s: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' => {
  switch (s) {
    case 'critico': return 'destructive';
    case 'alto': return 'warning';
    case 'medio': return 'secondary';
    default: return 'outline';
  }
};

export const EmpIndicadoresView: React.FC<Props> = ({ contratoId, contratoNome, canEdit }) => {
  const {
    indicadores, desvios, alertas, latest, isLoading,
    tratarDesvio, reconhecerAlerta, resolverAlerta, congelarIndicador,
  } = useEmpIndicadores(contratoId);
  const { recalcularEVM } = useEmpExecucaoMutations(contratoId);
  const { gerarEvmPdf, gerarSaldosPdf, loading: pdfLoading } = useGerarRelatorioPdf();

  const [desvioDialog, setDesvioDialog] = useState<EmpDesvio | null>(null);
  const [acaoText, setAcaoText] = useState('');
  const [resolucaoText, setResolucaoText] = useState('');
  const [alertaDialog, setAlertaDialog] = useState<EmpAlerta | null>(null);
  const [alertaNotas, setAlertaNotas] = useState('');

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // S-Curve data
  const sCurveData = indicadores.map(ind => ({
    periodo: fmtPeriodo(ind.periodo_referencia),
    'Previsto (%)': ind.percentual_fisico_previsto,
    'Realizado (%)': ind.percentual_fisico_realizado,
    'Financeiro Prev. (%)': ind.percentual_financeiro_previsto,
    'Financeiro Real. (%)': ind.percentual_financeiro_realizado,
  }));

  // SPI/CPI trend
  const trendData = indicadores.map(ind => ({
    periodo: fmtPeriodo(ind.periodo_referencia),
    SPI: ind.spi,
    CPI: ind.cpi,
  }));

  const desviosAbertos = desvios.filter(d => d.status === 'aberto' || d.status === 'em_tratamento');
  const alertasAtivos = alertas.filter(a => a.status === 'ativo' || a.status === 'reconhecido');

  // Auto-detect indicators needing freeze (SPI < 0.9)
  const needsFreeze = indicadores.filter(i => i.spi !== null && i.spi < 0.9 && !i.congelado);

  const handleTratarDesvio = () => {
    if (!desvioDialog || !acaoText.trim()) return;
    tratarDesvio.mutate({
      desvioId: desvioDialog.id,
      acao: acaoText,
      resolucao: resolucaoText.trim() || undefined,
    });
    setDesvioDialog(null);
    setAcaoText('');
    setResolucaoText('');
  };

  const handleResolverAlerta = () => {
    if (!alertaDialog || !alertaNotas.trim()) return;
    resolverAlerta.mutate({ alertaId: alertaDialog.id, notas: alertaNotas });
    setAlertaDialog(null);
    setAlertaNotas('');
  };

  return (
    <div className="space-y-6">
      {/* Header with recalculate + export */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Indicadores EVM</h3>
        <div className="flex gap-2">
          <EmpExportButton
            getData={() => indicadores.map(ind => ({
              'Período': fmtPeriodo(ind.periodo_referencia),
              'PV': ind.pv,
              'EV': ind.ev,
              'AC': ind.ac,
              'SPI': ind.spi,
              'CPI': ind.cpi,
              'EAC': ind.eac,
              'ETC': ind.etc,
              'VAC': ind.vac,
              '% Físico Prev.': ind.percentual_fisico_previsto,
              '% Físico Real.': ind.percentual_fisico_realizado,
              '% Financeiro Prev.': ind.percentual_financeiro_previsto,
              '% Financeiro Real.': ind.percentual_financeiro_realizado,
              'Desvio Prazo (dias)': ind.desvio_prazo_dias,
              'Congelado': ind.congelado ? 'Sim' : 'Não',
            }))}
            filename={`indicadores_evm_${contratoNome}`}
            printTitle={`Indicadores EVM — ${contratoNome}`}
            extraItems={[
              { label: 'EVM PDF', icon: FileDown, onClick: () => gerarEvmPdf(contratoId), disabled: pdfLoading },
              { label: 'Saldos PDF', icon: FileDown, onClick: () => gerarSaldosPdf(contratoId), disabled: pdfLoading },
            ]}
          />
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => recalcularEVM.mutate()}
              disabled={recalcularEVM.isPending}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${recalcularEVM.isPending ? 'animate-spin' : ''}`} />
              {recalcularEVM.isPending ? 'Calculando...' : 'Recalcular EVM'}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={evmCardBg(latest?.spi ?? null)}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Activity className="h-4 w-4" />
              <MetricTooltip explanation={METRIC_EXPLANATIONS.spi}>
                SPI (Schedule)
              </MetricTooltip>
            </div>
            <p className={`text-2xl font-bold ${spiColor(latest?.spi ?? null)}`}>
              {fmt(latest?.spi ?? null)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">Meta: 1,00</p>
            {latest?.spi !== null && latest?.spi !== undefined && latest.spi < 0.9 && (
              <p className="text-xs text-destructive mt-0.5">⚠ Abaixo do limiar</p>
            )}
          </CardContent>
        </Card>
        <Card className={evmCardBg(latest?.cpi ?? null)}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <BarChart3 className="h-4 w-4" />
              <MetricTooltip explanation={METRIC_EXPLANATIONS.cpi}>
                CPI (Cost)
              </MetricTooltip>
            </div>
            <p className={`text-2xl font-bold ${spiColor(latest?.cpi ?? null)}`}>
              {fmt(latest?.cpi ?? null)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">Meta: 1,00</p>
          </CardContent>
        </Card>
        <Card className={desviosAbertos.length > 0 ? 'bg-warning/5 border-warning/30' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <AlertTriangle className="h-4 w-4" /> Desvios Abertos
            </div>
            <p className="text-2xl font-bold">{desviosAbertos.length}</p>
          </CardContent>
        </Card>
        <Card className={alertasAtivos.length > 0 ? 'bg-destructive/5 border-destructive/30' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <ShieldAlert className="h-4 w-4" /> Alertas Ativos
            </div>
            <p className="text-2xl font-bold">{alertasAtivos.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Freeze warning */}
      {needsFreeze.length > 0 && canEdit && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-4 w-4 text-destructive" />
              <span className="font-medium text-destructive">
                {needsFreeze.length} indicador(es) com SPI &lt; 0.9 — requer congelamento
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {needsFreeze.map(ind => (
                <Button
                  key={ind.id}
                  variant="destructive"
                  size="sm"
                  onClick={() => congelarIndicador.mutate(ind.id)}
                  disabled={congelarIndicador.isPending}
                >
                  <Lock className="h-3 w-3 mr-1" />
                  Congelar {fmtPeriodo(ind.periodo_referencia)} (SPI={fmt(ind.spi)})
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="visao-geral" className="w-full">
        <TabsList>
          <TabsTrigger value="visao-geral">Visão Geral</TabsTrigger>
          <TabsTrigger value="curva-s">Curva S</TabsTrigger>
          <TabsTrigger value="desvios">Desvios ({desvios.length})</TabsTrigger>
          <TabsTrigger value="alertas">Alertas ({alertas.length})</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        {/* Visão Geral — SPI/CPI trend */}
        <TabsContent value="visao-geral">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Evolução SPI / CPI</CardTitle>
            </CardHeader>
            <CardContent>
              {trendData.length === 0 ? (
                <EmptyState text="Nenhum indicador EVM registrado neste contrato." icon={BarChart3} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="periodo" className="text-xs" />
                    <YAxis domain={[0, 'auto']} />
                    <Tooltip />
                    <Legend />
                    <ReferenceLine y={1} stroke="hsl(var(--muted-foreground))" strokeDasharray="5 5" label="Ideal" />
                    <ReferenceLine y={0.9} stroke="hsl(var(--destructive))" strokeDasharray="3 3" label="Limiar" />
                    <Line type="monotone" dataKey="SPI" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="CPI" stroke="hsl(var(--chart-2))" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Latest EVM summary */}
          {latest && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Resumo EVM — {fmtPeriodo(latest.periodo_referencia)}
                  {latest.congelado && <Badge variant="destructive"><Lock className="h-3 w-3 mr-1" /> Congelado</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-muted-foreground">PV:</span> <span className="font-medium">{fmt(latest.pv, 0)}</span></div>
                  <div><span className="text-muted-foreground">EV:</span> <span className="font-medium">{fmt(latest.ev, 0)}</span></div>
                  <div><span className="text-muted-foreground">AC:</span> <span className="font-medium">{fmt(latest.ac, 0)}</span></div>
                  <div><span className="text-muted-foreground">BAC:</span> <span className="font-medium">{fmt(latest.baseline_bac, 0)}</span></div>
                  <div><span className="text-muted-foreground">EAC:</span> <span className="font-medium">{fmt(latest.eac, 0)}</span></div>
                  <div><span className="text-muted-foreground">ETC:</span> <span className="font-medium">{fmt(latest.etc, 0)}</span></div>
                  <div><span className="text-muted-foreground">VAC:</span> <span className="font-medium">{fmt(latest.vac, 0)}</span></div>
                  <div><span className="text-muted-foreground">Desvio Prazo:</span> <span className="font-medium">{latest.desvio_prazo_dias} dias</span></div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-32 text-muted-foreground">
                      <MetricTooltip explanation={METRIC_EXPLANATIONS.progressoFisico}>
                        Físico Previsto
                      </MetricTooltip>
                    </span>
                    <Progress value={latest.percentual_fisico_previsto} className="flex-1 h-2" />
                    <span className="w-12 text-right">{fmt(latest.percentual_fisico_previsto, 1)}%</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-32 text-muted-foreground">
                      <MetricTooltip explanation={METRIC_EXPLANATIONS.progressoFisico}>
                        Físico Realizado
                      </MetricTooltip>
                    </span>
                    <Progress value={latest.percentual_fisico_realizado} className="flex-1 h-2" />
                    <span className="w-12 text-right">{fmt(latest.percentual_fisico_realizado, 1)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* S-Curve */}
        <TabsContent value="curva-s">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Curva S — Físico e Financeiro</CardTitle>
            </CardHeader>
            <CardContent>
              {sCurveData.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nenhum dado disponível.</p>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={sCurveData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="periodo" className="text-xs" />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Previsto (%)" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="5 5" />
                    <Line type="monotone" dataKey="Realizado (%)" stroke="hsl(var(--primary))" strokeWidth={2} />
                    <Line type="monotone" dataKey="Financeiro Prev. (%)" stroke="hsl(var(--chart-2))" strokeWidth={2} strokeDasharray="5 5" />
                    <Line type="monotone" dataKey="Financeiro Real. (%)" stroke="hsl(var(--chart-2))" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Desvios */}
        <TabsContent value="desvios">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Desvios Identificados</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {desvios.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nenhum desvio registrado.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Severidade</TableHead>
                      <TableHead>Desvio %</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      {canEdit && <TableHead className="text-right">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {desvios.map(d => (
                      <TableRow key={d.id}>
                        <TableCell>
                          <Badge variant="outline">{d.tipo}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{d.descricao}</TableCell>
                        <TableCell>
                          <Badge variant={sevBadge(d.severidade)}>{d.severidade}</Badge>
                        </TableCell>
                        <TableCell className={d.desvio_percentual && d.desvio_percentual < 0 ? 'text-destructive' : ''}>
                          {d.desvio_percentual !== null ? `${d.desvio_percentual.toFixed(1)}%` : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={d.status === 'resolvido' ? 'default' : d.status === 'em_tratamento' ? 'secondary' : 'outline'}>
                            {d.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{fmtDate(d.created_at)}</TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            {d.status !== 'resolvido' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setDesvioDialog(d); setAcaoText(d.acao_corretiva || ''); setResolucaoText(''); }}
                              >
                                Tratar
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alertas */}
        <TabsContent value="alertas">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Alertas do Contrato</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {alertas.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nenhum alerta.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Severidade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      {canEdit && <TableHead className="text-right">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alertas.map(a => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{a.titulo}</p>
                            {a.descricao && <p className="text-xs text-muted-foreground truncate max-w-[250px]">{a.descricao}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={sevBadge(a.severidade)}>{a.severidade}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            a.status === 'resolvido' ? 'default' :
                            a.status === 'reconhecido' ? 'secondary' : 'outline'
                          }>
                            {a.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{fmtDate(a.created_at)}</TableCell>
                        {canEdit && (
                          <TableCell className="text-right space-x-1">
                            {a.status === 'ativo' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => reconhecerAlerta.mutate(a.id)}
                                disabled={reconhecerAlerta.isPending}
                              >
                                <Eye className="h-3 w-3 mr-1" /> Reconhecer
                              </Button>
                            )}
                            {(a.status === 'ativo' || a.status === 'reconhecido') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setAlertaDialog(a); setAlertaNotas(''); }}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Resolver
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Histórico de indicadores */}
        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histórico de Indicadores</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {indicadores.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nenhum indicador registrado.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Período</TableHead>
                        <TableHead>SPI</TableHead>
                        <TableHead>CPI</TableHead>
                        <TableHead>Físico %</TableHead>
                        <TableHead>PV</TableHead>
                        <TableHead>EV</TableHead>
                        <TableHead>AC</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...indicadores].reverse().map(ind => (
                        <TableRow key={ind.id} className={ind.congelado ? 'opacity-60' : ''}>
                          <TableCell className="font-medium">{fmtPeriodo(ind.periodo_referencia)}</TableCell>
                          <TableCell className={spiColor(ind.spi)}>{fmt(ind.spi)}</TableCell>
                          <TableCell className={spiColor(ind.cpi)}>{fmt(ind.cpi)}</TableCell>
                          <TableCell>{fmt(ind.percentual_fisico_realizado, 1)}%</TableCell>
                          <TableCell>{fmt(ind.pv, 0)}</TableCell>
                          <TableCell>{fmt(ind.ev, 0)}</TableCell>
                          <TableCell>{fmt(ind.ac, 0)}</TableCell>
                          <TableCell>
                            {ind.congelado ? (
                              <Badge variant="destructive"><Lock className="h-3 w-3 mr-1" />Congelado</Badge>
                            ) : (
                              <Badge variant="outline">Ativo</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Deviation treatment dialog */}
      <Dialog open={!!desvioDialog} onOpenChange={() => setDesvioDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tratar Desvio</DialogTitle>
          </DialogHeader>
          {desvioDialog && (
            <div className="space-y-4">
              <div className="text-sm">
                <p><span className="text-muted-foreground">Tipo:</span> {desvioDialog.tipo}</p>
                <p><span className="text-muted-foreground">Descrição:</span> {desvioDialog.descricao}</p>
                {desvioDialog.desvio_percentual !== null && (
                  <p><span className="text-muted-foreground">Desvio:</span> {desvioDialog.desvio_percentual.toFixed(1)}%</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Ação Corretiva *</label>
                <Textarea value={acaoText} onChange={e => setAcaoText(e.target.value)} placeholder="Descreva a ação..." />
              </div>
              <div>
                <label className="text-sm font-medium">Resolução (preencha para encerrar)</label>
                <Textarea value={resolucaoText} onChange={e => setResolucaoText(e.target.value)} placeholder="Descrição da resolução..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDesvioDialog(null)}>Cancelar</Button>
            <Button onClick={handleTratarDesvio} disabled={!acaoText.trim() || tratarDesvio.isPending}>
              {resolucaoText.trim() ? 'Resolver' : 'Salvar Ação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert resolution dialog */}
      <Dialog open={!!alertaDialog} onOpenChange={() => setAlertaDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolver Alerta</DialogTitle>
          </DialogHeader>
          {alertaDialog && (
            <div className="space-y-4">
              <div className="text-sm">
                <p className="font-medium">{alertaDialog.titulo}</p>
                {alertaDialog.descricao && <p className="text-muted-foreground">{alertaDialog.descricao}</p>}
              </div>
              <div>
                <label className="text-sm font-medium">Notas de Resolução *</label>
                <Textarea value={alertaNotas} onChange={e => setAlertaNotas(e.target.value)} placeholder="Descreva a resolução..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertaDialog(null)}>Cancelar</Button>
            <Button onClick={handleResolverAlerta} disabled={!alertaNotas.trim() || resolverAlerta.isPending}>
              Resolver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
